import { connect } from 'react-redux';
import Cookies from 'js-cookie'

// Mui
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// Components
import LinkMenuItem from './MenuItem';
import { mapPreferencesToProps } from 'mappers/v1/preferences'

// Images
import LogoIcon from 'assets/images/logo/logomark.png';
import ComplianceIcon from 'assets/images/logo/compliance.png';
import RiskInsightsIcon from 'assets/images/logo/risk.png';
import VMIcon from 'assets/images/logo/vm.png';
import WorkspaceIcon from 'assets/images/logo/workspace.png';

const railsCookieConverter = { read: (value) => decodeURIComponent(value.replace(/\+/g, ' ')) }
const gatewayApplications = Cookies.withConverter(railsCookieConverter).get('gateway_applications') || null
const gatewayAppList = JSON.parse(gatewayApplications)?.map(app => ({
  appHref: app.frontendUrl + app.loginPath,
  icon: LogoIcon,
  infoHref: app.frontendUrl,
  text: app.productName + (app.instanceName ? ` (${app.instanceName})` : ''),
  productId: app.productId,
}))

const appLinks = {
  vm: {
    icon: VMIcon,
    text: 'Visitor Management',
    appHref: 'https://account.tractionguest.com/',
    infoHref: 'https://signinsolutions.com/visitor-management',
  },
  compliance: {
    icon: ComplianceIcon,
    text: 'Compliance',
    appHref: 'https://app.threatswitch.com/',
    infoHref: 'https://signinsolutions.com/risk-and-compliance',
  },
  workspace: {
    icon: WorkspaceIcon,
    text: 'Meetings',
    appHref: 'https://my.pronestorworkspace.com/',
    infoHref: 'https://signinsolutions.com/meetings-appointments',
  },
  riskInsights: {
    icon: RiskInsightsIcon,
    text: 'Risk Insights',
    infoHref: 'https://signinsolutions.com/risk-and-compliance',
  },
  signInSolutions: {
    icon: LogoIcon,
    text: 'Sign In Solutions',
    infoHref: 'https://signinsolutions.com/',
  },
};

const LinkSectionHeader = ({ text, paddingTop = '1.5rem' }) => (
  <Typography
    variant="subtitle"
    component="h3"
    sx={{
      paddingLeft: 2,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      fontWeight: 400,
      paddingTop,
    }}
  >
    {text}
  </Typography>
);

const LinkMenu = ({ anchorEl, preferences, profile, handleClose, open }) => {
  const { access_vm, access_workspace } = profile?.company || {};
  const access_vm_gateway = !!gatewayAppList?.find(({ productId }) => productId === 'SIE')

  return (
    <Menu
      anchorEl={anchorEl}
      id="apps-menu"
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          width: '15rem',
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <LinkSectionHeader text="Your apps" paddingTop="0.75rem" />

      {/*
        SIS Gateway only returns applications which the user currently has access to.
        Always show the full list to beta testers to make debugging easier (staging, localhost, etc.)
      */}
      {preferences?.betaTester && gatewayAppList?.map(app => (
        <LinkMenuItem key={app.appHref} enabled {...app} />
      ))}

      {(access_vm || access_vm_gateway) && <LinkMenuItem enabled {...appLinks.vm} />}
      <LinkMenuItem enabled {...appLinks.compliance} />
      {access_workspace && <LinkMenuItem enabled {...appLinks.workspace} />}

      {((access_vm || access_vm_gateway) && access_workspace) || <LinkSectionHeader text="Recommended apps" />}

      {(!access_vm && !access_vm_gateway) && <LinkMenuItem {...appLinks.vm} />}
      {!access_workspace && <LinkMenuItem {...appLinks.workspace} />}

      <Divider />

      <LinkSectionHeader text="Learn more" paddingTop="0.75rem" />

      <LinkMenuItem {...appLinks.riskInsights} />
      <LinkMenuItem {...appLinks.signInSolutions} />
    </Menu>
  );
};

export default connect(({ profile }) => ({ profile, ...mapPreferencesToProps({ profile }) }))(LinkMenu);
